/**
 * Centralized API error extractor.
 *
 * Handles the generic error response format returned by the backend:
 *
 * {
 *   "error": {
 *     "code": "RESOURCE_NOT_FOUND",
 *     "message": "Human-readable message",
 *     "details": { "additional": "context" },
 *     "timestamp": "2024-10-30T12:30:45.123456",
 *     "path": "/api/v1/endpoint"
 *   }
 * }
 *
 * Falls back gracefully to legacy response shapes (detail, message, plain error string).
 *
 * @param {any} err - The caught axios error object
 * @returns {string} A human-readable error message ready for display in the UI
 */
export function extractApiError(err) {
  const resp = err?.response?.data;

  if (!resp) {
    if (err?.request) return 'No response received from server. Please check your connection.';
    return err?.message || 'An unexpected error occurred.';
  }

  if (resp.error && typeof resp.error === 'object') {
    const errObj = resp.error;

    if (errObj.code === 'RATE_LIMIT_EXCEEDED') {
      return 'Too many attempts. Please wait a minute before trying again.';
    }

    // Check for validation errors nested inside details
    const validationErrors = errObj.details?.validation_errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      return validationErrors
        .map((e) => e.message || e.msg)
        .filter(Boolean)
        .join('. ');
    }

    // Main human-readable message from the generic format
    if (typeof errObj.message === 'string' && errObj.message) return errObj.message;
    
    // Fallback if message is missing but code exists
    if (typeof errObj.code === 'string' && errObj.code) return `Error: ${errObj.code}`;
  }

  // Priority 2: direct message field
  if (typeof resp.message === 'string' && resp.message) return resp.message;

  // Priority 3: detail field (legacy / Pydantic validation errors)
  const detail = resp.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first.msg === 'string') return first.msg;
    if (typeof first === 'string') return first;
  }

  // Priority 4: error as plain string (older format)
  if (typeof resp.error === 'string') return resp.error;

  // Final fallbacks
  const finalMessage = err?.message || 'An unexpected error occurred.';
  return typeof finalMessage === 'string' ? finalMessage : String(finalMessage);
}

